import React from 'react';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import BusComponent from '../../BusComponent';
import Orientation from "../../../enums/Orientation";
import AutoAdjustButton from "./autoAdjust/AutoAdjustButton";
import AutoAdjustMenu from "./autoAdjust/AutoAdjustMenu";
import ApplicationMode from "../../../enums/ApplicationMode";
import {getMetadataObjectsFromVariableSelectionItem} from "../../../helpers/Util";
import VariableType from "../../../enums/VariableType";
import ThreeDotsLoader from "../../ThreeDotsLoader";
import ColorPaletteList
    from "../../visualizationEditor/selectors/colorPaletteSelector/colorPaletteList/ColorPaletteList";
import Panel from "../../panel/Panel";
import LegendLayout from "../../../enums/LegendLayout";
import GeographyLevelDropdown from "./GeographyLevelDropdown";
import Tooltip from "../../Tooltip";
import ColorPaletteVisualization from "./ColorPaletteVisualization";

class VisualizationEditor extends BusComponent {
    constructor(props, context) {
        super(props, context);

        this.state = {
            showLoader: false,
            autoAdjustEnabled: false,
            colorPaletteListVisible: false,
            visualizationType: undefined,
            bubbleVisualizationTypeEnabled: false,
            shadedVisualizationTypeEnabled: false,
            dotDensityVisualizationTypeEnabled: false,
            selectedColorPalette: undefined,
            layout: LegendLayout.SIMPLIFIED,
        };

        this.bindGluBusEvents({
            MAP_NEW_COLOR_PALETTE_APPLIED: this.onMapNewColorPaletteApplied,
            MAP_COLOR_PALETTES_CHANGED: this.onColorPaletteListChanged,
            CURRENT_COLOR_PALETTES: this.onCurrentColorPalettesRetrieved,
            MAP_NEW_DATA_THEME_APPLIED: this.onMapNewDataThemeApplied,
            VISUALIZATION_TYPE_AVAILABILITY_INFO: this.onVisualizationTypeAvailabilityInfo,
            MAP_LIBRARY_LAYERS_UPDATED: this.onLibraryLayersUpdated,
        });

        this.paletteRef = null;
        this.setPaletteRef = element => {
            this.paletteRef = element;
        };
    }

    componentDidMount() {
        this.bindGluBusEvents({
            AUTO_ADJUST_FAILED_POPUP_REQUEST: this.hideLoader,
            APPLY_NATURAL_BREAKS_CUTPOINTS_DONE: this.hideLoader,
            DATA_ANALYSIS_DATA_REQUEST_DONE: this.hideLoader, // bubbles auto-adjust done event
            MAP_NEW_DATA_THEME_APPLIED: this.updateAutoAdjust,
            CURRENT_METADATA: this.onCurrentMetadataRetrieved,
        });

        this.emit('MAP_CLEAR_ALL_HIGHLIGHT_RULES');
        this.emit('CURRENT_METADATA_REQUEST', { source: this });
    }

    componentWillMount() {
        const { mapInstance } = this.props;

        document.addEventListener("mousedown", this.handleClickOutside);
        this.emit('GET_VISUALIZATION_TYPE_AVAILABILITY_INFO_REQUEST', mapInstance.dataTheme.variableSelection);
        this.emit('MAP_GET_CURRENT_COLOR_PALETTES_REQUEST', { mapInstanceId: mapInstance.id });
    }

    componentWillReceiveProps(nextProps) {
        const { mapInstance } = this.props;

        if (mapInstance !== nextProps.mapInstance) {
            this.emit('GET_VISUALIZATION_TYPE_AVAILABILITY_INFO_REQUEST', nextProps.mapInstance.dataTheme.variableSelection);
            this.emit('MAP_GET_CURRENT_COLOR_PALETTES_REQUEST', { mapInstanceId: nextProps.mapInstance.id });
        }
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);

        this.unbindGluBusEvents();
    }

    handleClickOutside = (event) => {
        if (this.paletteRef && !this.paletteRef.contains(event.target)) {
            this.forcePalettePopupClose();
        }
    }

    componentDidUpdate(prevProps) {
        const { mapInstance } = this.props;
        if (prevProps.mapInstance.id !== mapInstance.id) {
            this.getVariableMetadata();
        }
    }

    forcePalettePopupClose = () => {
        this.setState( {colorPaletteListVisible: false });
    }

    togglePalettePopup = () => {
        this.setState((prevState) => ({
            colorPaletteListVisible: !prevState.colorPaletteListVisible,
        }));
    }

    onMapNewColorPaletteApplied(e) {
        const { mapInstance } = this.props;

        if (e.source.id === mapInstance.id) {
            this.setState({
                selectedColorPalette: e.selectedColorPalette,
            });
            this.getVariableMetadata();
        }
    }

    onColorPaletteListChanged(e) {
        const { mapInstance } = this.props;

        this.setState({
            colorPalettesCollection: e.colorPalettesCollection,
            selectedColorPalette: e.selectedColorPalette,
        });
        if (e.source.id === mapInstance.id) {
            this.getVariableMetadata();
        }
    }

    onCurrentColorPalettesRetrieved = e => {
        const { mapInstance } = this.props;

        if (mapInstance.id === e.mapInstanceId) {
            this.setState({
                selectedColorPalette: e.selectedColorPalette,
                colorPalettesCollection: e.colorPalettesCollection,
            });
        }
    }

    onVisualizationTypeAvailabilityInfo(e) {
        this.setState({
            bubbleVisualizationTypeEnabled: e.bubbleVisualizationTypeEnabled,
            shadedVisualizationTypeEnabled: e.shadedVisualizationTypeEnabled,
            dotDensityVisualizationTypeEnabled: e.dotDensityVisualizationTypeEnabled,
        });
    }

    onMapNewDataThemeApplied(e) {
        const { mapInstance } = this.props;

        if (e.source.id === mapInstance.id) {
            this.emit('GET_VISUALIZATION_TYPE_AVAILABILITY_INFO_REQUEST', mapInstance.dataTheme.variableSelection);
            this.emit('MAP_GET_CURRENT_COLOR_PALETTES_REQUEST', { mapInstanceId: mapInstance.id });
            this.getVariableMetadata();
        }
    }

    onApplyColorPalette = palette => {
        const { mapInstance } = this.props;

        if (palette) {
            this.emit('APPLY_COLOR_PALETTE_REQUEST', {
                source: this,
                mapInstanceId: mapInstance.id,
                colorPalette: palette,
            });
            this.togglePalettePopup();
        }
    }

    onEditPaletteClick = palette => {
        const { mapInstance } = this.props;
        const { colorPalettesCollection, paletteEditorType } = this.state;

        this.emit('COLOR_PALETTE_POPUP_REQUEST', {
            paletteEditorType: paletteEditorType,
            colorPalette: palette,
            colorPalettesList: colorPalettesCollection,
            mapInstanceId: mapInstance.id,
            editMode: true,
        });
        this.emit('CLOSE_DROPDOWN');
    }

    onAutoAdjustClick = () => {
        const { mapInstance } = this.props;

        this.emit('APPLY_NATURAL_BREAKS_CUTPOINTS_REQUEST',
            { mapInstanceId: mapInstance.id }
        );
    }

    applyAutoAdjust = () => {
        this.setState({ showLoader: true }, () => {
            // Sometimes this.props.onAutoAdjust function runs before React manages to render the new state (with showLoader: true).
            // In the meantime onAutoAdjust takes up all the browser memory and freezes the browser. Consequently the `showLoader` state
            // is updated and rendered after all auto adjust work is done (which can last for multiple seconds).
            // So I force the `onAutoAdjust` to happen after the loader is rendered - using setTimeout.
            //
            // TODO: Right now the natural cutpoints auto adjust can be very resource consuming.
            // There are scenarios where user can select block groups and attempt to do the auto adjust
            // with more than 30k features. We allow 5k features right now but it is still hard on the browser
            // to handle that - so it freezes and blocks the main thread.
            // That's why we need to handle that heavy logic inside a separate thread - using worker
            // ( https://socex-charts.atlassian.net/browse/SOCEX-1658 )
            // Once that is implemented, there will be no need for the `setTimeout`
            setTimeout(() => this.onAutoAdjustClick());
        });
    }

    updateAutoAdjust() {
        this.emit('CURRENT_METADATA_REQUEST', {
            source: this
        });
    }

    hideLoader = () => this.setState({ showLoader: false });

    onCurrentMetadataRetrieved = (metadata, target) => {
        const { mapInstance } = this.props;

        if (target === this) {
            const currentMetadataObject = getMetadataObjectsFromVariableSelectionItem(
                mapInstance.dataTheme.variableSelection.items[0],
                metadata
            );
            // Variable type NONE is for variables that are categorical and do not support
            // auto adjust functionality
            this.setState({
                autoAdjustEnabled: currentMetadataObject.variable.varType !== VariableType.NONE,
                showPercentageControl: currentMetadataObject.variable.showPercentageControl,
            });
        }
    };

    onLibraryLayersUpdated = () => {
        // Toggle on visibility of the layer doesn't update DOM if you remove
        // this line.
        this.forceUpdate();
    }

    render() {
        const { intl, mapInstance, onShowDataBrowser } = this.props;
        const { applicationMode } = this.context;
        const {
            showLoader,
            autoAdjustEnabled,
            colorPaletteListVisible,
            colorPalettesCollection,
            selectedColorPalette,
            showPercentageControl,
            layout
        } = this.state;

        if (!autoAdjustEnabled) {
            return null;
        }

        //Auto-adjust button should be disabled if multiple layers are selected
        //and the selected values are presented in bubble visualization mode
        const layersForBubbleVisualization = mapInstance.dataTheme.rendering[0].dotValueFieldNames;
        const isAutoAdjustButtonVisible = layersForBubbleVisualization ? layersForBubbleVisualization.length === 1 : true;

        const autoAdjustButton = showLoader
            ? <ThreeDotsLoader />
            : <AutoAdjustButton
                mapInstance={mapInstance}
                onAutoAdjust={this.applyAutoAdjust}
                orientation={Orientation.LEFT}
            />

        const changeDataButton = (
            <div className="basemap-editor__visualization-editor-container__btn-container small">
                <Tooltip
                    placement="bottom"
                    mouseEnterDelay={0.5}
                    mouseLeaveDelay={0}
                    overlay={intl.formatMessage({id: 'dataBrowser.changeData'})}
                >
                    <button
                        className="basemap-editor__visualization-editor-container__btn-container__btn justify-center"
                        onClick={onShowDataBrowser}
                        ref={e => { this.trigger = e; }}
                    >
                        <span> {intl.formatMessage({id: 'dataBrowser.change' })} </span>
                    </button>
                </Tooltip>
            </div>
        );

        const visualizationLegendClassNames = classNames(
            'visualization-legend--shaded horizontal-shaded-area-legend flex-it column grow justify-center', {
                'visualization-legend--simple': layout === LegendLayout.SIMPLIFIED,
                'visualization-legend--detailed': layout === LegendLayout.DETAILED,
        });

        const canEditVisualization = applicationMode === ApplicationMode.EXPLORE || applicationMode === ApplicationMode.EDIT;
        const emptyHeader = <div style={{display: 'none'}}/>;

        return (
            <div className="basemap-editor__visualization-editor-container">
                <div className="basemap-editor__visualization-editor-container__content">
                    <div className="basemap-editor__visualization-editor-container__content__title">
                        {mapInstance.dataTheme.title}
                        {changeDataButton}
                    </div>
                    <div className={visualizationLegendClassNames}>
                        <ColorPaletteVisualization mapInstance={mapInstance} />
                    </div>
                    <div className="flex-it grow row center space-between">
                        <GeographyLevelDropdown mapInstance={mapInstance}/>
                        {isAutoAdjustButtonVisible && autoAdjustButton}
                        {canEditVisualization && (
                            <div className="flex-it center" ref={this.setPaletteRef}
                            >
                                <AutoAdjustMenu
                                    mapInstance={mapInstance}
                                    showPercentageControl={showPercentageControl}
                                    togglePalettePopup={this.togglePalettePopup}
                                    forcePalettePopupClose={this.forcePalettePopupClose}
                                />

                                {colorPaletteListVisible && (
                                    <Panel
                                        customHeader={emptyHeader}
                                        className="panel--color-palette"
                                    >
                                        <ColorPaletteList
                                            colorPalettesCollection={colorPalettesCollection}
                                            selectedColorPalette={selectedColorPalette}
                                            mapInstanceId={mapInstance.id}
                                            onColorPaletteClick={this.onApplyColorPalette}
                                            onEditPaletteClick={this.onEditPaletteClick}
                                        />
                                    </Panel>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    getVariableMetadata = () => {
        const { mapInstance } = this.props;
        this.emit('VARIABLE_SELECTION_METADATA_INFO_REQUEST', {
            source: this,
            mapInstanceId: mapInstance.id,
            dataDictionaryInfo: true,
            variableSelectionItems: mapInstance.dataTheme.variableSelection.items,
        });
    }
}

VisualizationEditor.propTypes = {
    mapInstance: PropTypes.object.isRequired,
    onShowDataBrowser: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(VisualizationEditor);
